import React, { Fragment, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DropdownList, Multiselect } from 'react-widgets';
import { TextInput, DateInput } from '../../../components/form-group';
import ErrorMessage from '../../../components/text/ErrorMessage';
import { addSpacesToEnumValue } from '../../../utils/common';
import { InteriorType } from '../../../core/api/models/requirement';
import { toastr } from 'react-redux-toastr';

const SearchRequirement: React.FC<any> = (props) => {
  const {
    data,
    onSubmit,
    cities,
    microMarketsData,
    preferredLocationsData,
    projectType,
    propertyType,
    floorPreferences,
    facilities,
    handleCityZones,
    canReset,
    setCanReset,
    disableAll,
    hideButtons,
    isSaveModalOpen,
    saving,
  } = props;

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    errors,
    watch,
    trigger,
    formState: { isDirty },
  } = useForm({
    defaultValues: data,
  });

  React.useEffect(() => {
    if (canReset) {
      reset(data);
      setCanReset(false);
    }
  }, [canReset]);

  const [advancedSearch, setAdvancedSearch] = React.useState<boolean>(false);

  useEffect(() => {
    if (!isSaveModalOpen && advancedSearch) {
      setAdvancedSearch(false);
    }
  }, [isSaveModalOpen]);

  const minArea = watch('minArea');
  const maxArea = watch('maxArea');
  const minPrice = watch('minPrice');
  const maxPrice = watch('maxPrice');
  const minSeats = watch('minSeats');
  const maxSeats = watch('maxSeats');
  const selectedPropertyType =
    propertyType?.find(
      (prptyType: { value: any }) => prptyType.value === watch('propertyType')
    )?.name || '';
  const selectedProjectType =
    projectType?.find(
      (projType: { value: any }) => projType.value === watch('projectType')
    )?.name || '';
  const selectedConstrutionStatus = watch('constructionStatus') || [];

  useEffect(() => {
    trigger('minArea');
    trigger('maxArea');
    trigger('minPrice');
    trigger('maxPrice');
    trigger('minSeats');
    trigger('maxSeats');
  }, [
    watch('minArea'),
    watch('maxArea'),
    watch('minPrice'),
    watch('maxPrice'),
    watch('minSeats'),
    watch('maxSeats'),
  ]);

  enum ConstructionStatus {
    Upcoming = 'upcoming',
    ReadyToMove = 'readyToMove',
    UnderConstruction = 'underConstruction',
    Operational = 'operational',
    ReadyForFitOuts = 'readyForFitOuts',
  }

  let advancedFilterCount = 0;
  if (minArea || maxArea) {
    advancedFilterCount = advancedFilterCount + 1;
  }
  if (minSeats || maxSeats) {
    advancedFilterCount = advancedFilterCount + 1;
  }

  if (minPrice || maxPrice) {
    advancedFilterCount = advancedFilterCount + 1;
  }

  if (watch('earliestMoveInDate') || watch('lastMoveInDate')) {
    advancedFilterCount = advancedFilterCount + 1;
  }

  if (watch('floorPreferences')?.length) {
    advancedFilterCount = advancedFilterCount + 1;
  }
  if (watch('preferedProjects')?.length) {
    advancedFilterCount = advancedFilterCount + 1;
  }

  if (watch('preferredDevelopers')?.length) {
    advancedFilterCount = advancedFilterCount + 1;
  }

  if (watch('constructionStatus')?.length) {
    advancedFilterCount = advancedFilterCount + 1;
  }
 
  if (watch('amenities')?.length) {
    advancedFilterCount = advancedFilterCount + 1;
  }
  if (watch('facilityType')?.length) {
    advancedFilterCount = advancedFilterCount + 1;
  }
  if (watch('interiorType')?.length) {
    advancedFilterCount = advancedFilterCount + 1;
  }
  if (watch('constructionCompletionDate')) {
    advancedFilterCount = advancedFilterCount + 1;
  }
  if (watch('includeGradeC')) {
    advancedFilterCount = advancedFilterCount + 1;
  }
  if (watch('includeLeaseExpiry')) {
    advancedFilterCount = advancedFilterCount + 1;
  }

  return (
    <form
      className='w-100'
      onSubmit={handleSubmit((dat) => {
        if (!isDirty) {
          toastr.error('Error', 'No changes have been made');
        } else {
          onSubmit(dat);
        }
      })}
    >
      <div className='row gutter-8'>
        <div className='col-4 mb-2 pb-1'>
          <label className='font-sm font-weight-semibold mb-1' htmlFor='city'>
            City:
          </label>
          <Controller
            name='city'
            control={control}
            rules={{
              required: {
                value: true,
                message: 'City is required',
              },
            }}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                data={cities}
                busy={cities?.length === 0}
                textField='name'
                valueField={'value'}
                filter='contains'
                onChange={(val) => {
                  onChange(val);
                  handleCityZones(val?.value);
                  setValue('microMarkets', []);
                  setValue('preferredLocations', []);
                }}
                placeholder='Select City'
                disabled
              />
            )}
          />
          {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
        </div>

        {!!microMarketsData?.length && (
          <div className='col-4 mb-2 pb-1'>
            <label
              className='font-sm font-weight-semibold mb-1'
              htmlFor='microMarkets'
            >
              Micro Markets:
            </label>
            <Controller
              name='microMarkets'
              control={control}
              rules={{
                required: 'Micro Markets is required',
                validate: (value) => {
                  return !!(value.length > 0);
                },
              }}
              defaultValue={props.data.microMarkets}
              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={microMarketsData}
                  textField={'name'}
                  valueField={'value'}
                  onChange={(val) => {
                    onChange(val?.map((x) => x.value));
                  }}
                  placeholder='Search'
                  disabled
                />
              )}
            />
            {errors.microMarkets && (
              <ErrorMessage>Micro Markets is required</ErrorMessage>
            )}
          </div>
        )}
        {!!preferredLocationsData?.length && (
          <div className='col-4 mb-2 pb-1'>
            <label
              className='font-sm font-weight-semibold mb-1'
              htmlFor='preferredLocations'
            >
              Preferred Locations:
            </label>
            <Controller
              name='preferredLocations'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Preferred Locations is required',
                },
                validate: (value) => {
                  return !!(value.length > 0);
                },
              }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={preferredLocationsData}
                  textField={'name'}
                  valueField={'value'}
                  onChange={(val) => {
                    onChange(val?.map((x) => x.value));
                  }}
                  placeholder='Search Locations'
                  disabled
                />
              )}
            />
            {errors.preferredLocations && (
              <ErrorMessage>Preferred Locations is required</ErrorMessage>
            )}
          </div>
        )}

        <div className='col-4 mb-2 pb-1'>
          <label
            className='font-sm font-weight-semibold mb-1'
            htmlFor='propertyType'
          >
            Property Type:<span className='text-danger'>*</span>
          </label>
          <Controller
            name='propertyType'
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Property Type is required',
              },
            }}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                data={propertyType}
                textField={'name'}
                valueField={'value'}
                onChange={(val) => {
                  onChange(val);
                }}
                placeholder='Select Property Type'
                disabled
              />
            )}
          />
          {errors.propertyType && (
            <ErrorMessage>{errors.propertyType.message}</ErrorMessage>
          )}
        </div>

        <div className='col-4 mb-2 pb-1'>
          <label
            className='font-sm font-weight-semibold mb-1'
            htmlFor='projectType'
          >
            Project Type:<span className='text-danger'>*</span>
          </label>
          <Controller
            name='projectType'
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Project Type is required',
              },
            }}
            render={({ onChange, value }) => (
              <Multiselect
                value={value}
                data={projectType}
                textField={'name'}
                valueField={'value'}
                onChange={(val) => {
                  onChange(val);
                }}
                placeholder='Search Project Types'
                disabled
              />
            )}
          />
          {errors.projectType && (
            <ErrorMessage>{errors.projectType.message}</ErrorMessage>
          )}
        </div>

        <div className='col-4 mb-2 pb-1 d-flex flex-column justify-content-between'>
          <label className='font-sm font-weight-semibold mb-1 d-block'>
            &nbsp;
          </label>
          <div className='align-items-center d-flex'>
            {!advancedSearch && !hideButtons && (
              <button
                type='submit'
                className='btn btn-outline-primary font-normal font-weight-bold px-4 rounded'
                style={{ height: '43px' }}
              >
                Search
              </button>
            )}

            <button
              type="button"
              className="btn text-primary font-xs font-weight-semibold text-uppercase d-flex align-items-center"
              onClick={() => {
                setAdvancedSearch(!advancedSearch);
                props.setAdvancedSearch(!advancedSearch);
              }}
            >
              <i className='icon-filter mr-1'></i>
              Advanced Filter{' '}
              <span className='count-filter bg-primary text-white rounded-circle d-flex align-items-center justify-content-center ml-1'>
                {advancedFilterCount}
              </span>
              <i
                className={`${
                  advancedSearch ? 'icon-chevron-up' : 'icon-chevron-down'
                } ml-4`}
              ></i>
            </button>
            {!hideButtons && (
              <span className='font-normal font-weight-semibold text-gray-500'>
                Reset
              </span>
            )}
          </div>
        </div>
      </div>
      {advancedSearch && (
        <div className="col-12 row mt-5 align-items-end">
          <div className="col-12 p-3 font-normal font-weight-semibold bg-gray-100 mt-3 mb-4 ">
            Additional Property Information
          </div>

          {(selectedPropertyType === "Business Center & Co-working Space" ||
            selectedProjectType === "Business Center & Co-working Space") && (
              <div className="col-4 mb-4">
                <div className="row">
                  <div className="col-12 font-normal font-weight-semibold mb-3">
                    Select Seats:
                  </div>
                  <div className="col-6 textInput-component-style">
                    <label
                      className='font-sm font-weight-semibold mb-1'
                      htmlFor='minSeats'
                    >
                      Min Seats:
                    </label>
                    <Controller
                      name='minSeats'
                      control={control}
                      rules={{
                        required: {
                          value:
                            selectedPropertyType ===
                              'Business Center & Co-working Space' ||
                            selectedProjectType ===
                              'Business Center & Co-working Space'
                              ? true
                              : false,
                          message: 'Min Seats is required',
                        },
                        validate: (value) => {
                          const numericMaxSeats = maxSeats;
                          if (
                            numericMaxSeats &&
                            (value === undefined ||
                              value === null ||
                              value === '')
                          ) {
                            return 'Min Seats is required';
                          }

                          return undefined;
                        },
                      }}
                      render={({ onChange, value, name }) => (
                        <TextInput
                          type='number'
                          placeholder='Min Seats'
                          onChange={onChange}
                          name={name}
                          default={value}
                          gap='no'
                          clearFeildValue={true}
                        />
                      )}
                    />
                    {errors.minSeats && (
                      <ErrorMessage>{errors.minSeats.message}</ErrorMessage>
                    )}
                  </div>
                  <div className="col-6 textInput-component-style">
                    <label
                      className='font-sm font-weight-semibold mb-1'
                      htmlFor='maxSeats'
                    >
                      Max Seats:
                    </label>

                    <Controller
                      name='maxSeats'
                      control={control}
                      rules={{
                        required: {
                          value:
                            selectedPropertyType ===
                            'Business Center & Co-working Space'
                              ? true
                              : false,
                          message: 'Max Seats is required',
                        },
                        validate: (value) => {
                          const numericValue = value;
                          const tempMinSeats = minSeats;
                          return (tempMinSeats || tempMinSeats === 0) &&
                            numericValue !== 0 &&
                            !numericValue
                            ? 'Max Seats is required'
                            : (!numericValue && numericValue !== 0) ||
                                (!tempMinSeats && tempMinSeats !== 0) ||
                                (numericValue && numericValue > tempMinSeats) ||
                                (numericValue === 0 && tempMinSeats === 0) ||
                                'Max Seats should be greater than Min Seats';
                        },
                      }}
                      render={({ onChange, value, name }) => (
                        <TextInput
                          type='number'
                          placeholder='Max Seats'
                          onChange={onChange}
                          name={name}
                          default={value}
                          gap='no'
                          clearFeildValue={true}
                        />
                      )}
                    />
                    {errors.maxSeats && (
                      <ErrorMessage>{errors.maxSeats.message}</ErrorMessage>
                    )}
                  </div>
                </div>
              </div>
            )}

          <div className='col-4 mb-4'>
            <div className='row'>
              <div
                className='col-12 font-normal font-weight-semibold mb-3'
              >
                Select Budget(Per Sq. Ft.)
              </div>
              <div className="col-6 textInput-component-style">
                <label
                  className='font-sm font-weight-semibold mb-1'
                  htmlFor='minPrice'
                >
                  Min Price:
                </label>
                <Controller
                  name='minPrice'
                  control={control}
                  rules={{
                    validate: (value) => {
                      const numericMaxPrice = maxPrice;
                      if (
                        numericMaxPrice &&
                        (value === undefined || value === null || value === '')
                      ) {
                        return 'Min Price is required';
                      }

                      return undefined;
                    },
                  }}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type='number'
                      placeholder='Min Price'
                      onChange={onChange}
                      name={name}
                      default={value}
                      disabled={disableAll}
                      gap='no'
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.minPrice && (
                  <ErrorMessage>{errors.minPrice.message}</ErrorMessage>
                )}
              </div>

              <div className="col-6 textInput-component-style">
                <label
                  className='font-sm font-weight-semibold mb-1'
                  htmlFor='maxPrice'
                >
                  Max Price:
                </label>

                <Controller
                  name='maxPrice'
                  control={control}
                  rules={{
                    validate: (value) => {
                      const numericValue = value;
                      const tempMinPrice = minPrice;
                      return (tempMinPrice || tempMinPrice === 0) &&
                        numericValue !== 0 &&
                        !numericValue
                        ? 'Max Price is required'
                        : (!numericValue && numericValue !== 0) ||
                            (!tempMinPrice && tempMinPrice !== 0) ||
                            (numericValue && numericValue > tempMinPrice) ||
                            (numericValue === 0 && tempMinPrice === 0) ||
                            'Max Price must be greater than Min Price';
                    },
                  }}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type='number'
                      placeholder='Max Price'
                      onChange={onChange}
                      name={name}
                      default={value}
                      disabled={disableAll}
                      gap='no'
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.maxPrice && (
                  <ErrorMessage>{errors.maxPrice.message}</ErrorMessage>
                )}
              </div>
            </div>
          </div>
          <div className='col-4 mb-4'>
            <div className='row'>
              <div className='col-12 font-normal font-weight-semibold  mb-3'>
                Area Offered(Sq. Ft.)
              </div>

              <div className="col-6 textInput-component-style">
                <label
                  className='font-sm font-weight-semibold mb-1'
                  htmlFor='minArea'
                >
                  Min Area:
                </label>
                <Controller
                  name='minArea'
                  control={control}
                  rules={{
                    validate: (value) => {
                      const numericMaxArea = maxArea;
                      if (
                        numericMaxArea &&
                        (value === undefined || value === null || value === '')
                      ) {
                        return 'Min Area is required';
                      }

                      return undefined;
                    },
                  }}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type='number'
                      placeholder='Min Area'
                      onChange={onChange}
                      name={name}
                      default={value}
                      gap='no'
                      disabled={disableAll}
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.minArea && (
                  <ErrorMessage>{errors.minArea.message}</ErrorMessage>
                )}
              </div>

              <div className="col-6 textInput-component-style">
                <label
                  className='font-sm font-weight-semibold mb-1'
                  htmlFor='maxArea'
                >
                  Max Area:
                </label>
                <Controller
                  name='maxArea'
                  control={control}
                  rules={{
                    validate: (value) => {
                      const numericValue = value;
                      const tempMinArea = minArea;
                      return (tempMinArea || tempMinArea === 0) &&
                        numericValue !== 0 &&
                        !numericValue
                        ? 'Max Area is required'
                        : (!numericValue && numericValue !== 0) ||
                            (!tempMinArea && tempMinArea !== 0) ||
                            (numericValue && numericValue > tempMinArea) ||
                            (numericValue === 0 && tempMinArea === 0) ||
                            'Max Area must be greater than Min Area';
                    },
                  }}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type='number'
                      placeholder='Max Area'
                      onChange={onChange}
                      name={name}
                      default={value}
                      gap='no'
                      disabled={disableAll}
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.maxArea && (
                  <ErrorMessage>{errors.maxArea.message}</ErrorMessage>
                )}
              </div>
            </div>
          </div>
          <div className="col-4 mb-4">
            <div className="row">
              <div className="col-12 font-normal font-weight-semibold mb-2">
                Move-in Date
              </div>
              <div className='col-6'>
                <>
                  <Controller
                    control={control}
                    name='earliestMoveInDate'
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder='Earliest Move in Date'
                        onChange={onChange}
                        disabled={disableAll}
                      />
                    )}
                  />
                  {errors.earliestMoveInDate && (
                    <ErrorMessage>
                      {errors.earliestMoveInDate.message}
                    </ErrorMessage>
                  )}
                </>
              </div>
              <div className='col-6'>
                <>
                  <Controller
                    control={control}
                    rules={{
                      validate: {
                        dateComparison: (value) => {
                          const earliestMoveInDate = watch(
                            'earliestMoveInDate'
                          ); // Get the value of the "lastMoveInDate" field
                          if (earliestMoveInDate && value) {
                            return (
                              new Date(earliestMoveInDate) < new Date(value) ||
                              'Last Move-in Date should be greater than Earliest Move-in Date'
                            );
                          }
                        },
                      },
                    }}
                    name='lastMoveInDate'
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder='Last Move-in Date'
                        onChange={onChange}
                        disabled={disableAll}
                      />
                    )}
                  />
                  {errors.lastMoveInDate && (
                    <ErrorMessage>{errors.lastMoveInDate.message}</ErrorMessage>
                  )}
                </>
              </div>
            </div>
          </div>
          <div className="col-4 mb-4">
            <div className="row">
              {!!floorPreferences?.length && (
                <>
                  <div className="col-12 font-normal font-weight-semibold mb-2">
                    Floor Preferences
                  </div>
                  <div className='col-12 '>
                    <Controller
                      name='floorPreferences'
                      control={control}
                      render={({ onChange, value }) => (
                        <Multiselect
                          value={value}
                          data={floorPreferences}
                          textField={'name'}
                          valueField={'value'}
                          onChange={(val) => {
                            onChange(val?.map((x) => x.value));
                          }}
                          filter='startsWith'
                          messages={
                            floorPreferences?.length === 0
                              ? { emptyList: 'No data found' }
                              : {}
                          }
                          placeholder='Select Floor'
                          disabled={disableAll}
                        />
                      )}
                    />
                    {errors.floorPreferences && (
                      <ErrorMessage>Floor Preferences is required</ErrorMessage>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="col-4 mb-4">
            <div className="row">
              <div className="col-12">
                <label className="font-normal font-weight-semibold mb-2">
                  Construction Status
                </label>
                <Controller
                  name='constructionStatus'
                  control={control}
                  render={({ onChange, value }) => (
                    <Multiselect
                      value={value}
                      data={Object.entries(ConstructionStatus).map(
                        ([name, key]) => ({
                          name: addSpacesToEnumValue(name),
                          value: key,
                        })
                      )}
                      textField='name'
                      valueField='value'
                      onChange={(val) => {
                        onChange(val?.map((x) => x.value));
                        const selectedValues = val?.map((x) => x.value) || [];
                        if (
                          !selectedValues?.includes(
                            ConstructionStatus.UnderConstruction
                          )
                        ) {
                          setValue('constructionCompletionDate', '');
                        }
                      }}
                      placeholder='Select'
                      disabled={disableAll}
                    />
                  )}
                />
                {errors.constructionStatus && (
                  <ErrorMessage>Construction Status is required</ErrorMessage>
                )}
              </div>
            </div>
          </div>

          {selectedConstrutionStatus &&
            selectedConstrutionStatus?.some(
              (item: string) => item === ConstructionStatus.UnderConstruction
            ) && (
              <div className='col-4 mb-4'>
                <label className='font-normal font-weight-semibold'>
                  Construction Completion Date
                </label>
                <Controller
                  control={control}
                  name='constructionCompletionDate'
                  render={({ onChange, value }) => (
                    <DateInput
                      default={value}
                      placeholder='Date'
                      onChange={(val) => {
                        onChange(val);
                      }}
                      minDate={new Date()}
                    />
                  )}
                />
              </div>
            )}
          {(selectedPropertyType === "Business Center & Co-working Space" ||
            selectedProjectType === "Business Center & Co-working Space") && <div className="col-4 mb-4">
              <div className="row">
                <div className="col-12">
                  <label
                    className="font-normal font-weight-semibold mb-2"
                    htmlFor="facilityType"
                  >
                    Facility Type
                  </label>
                </div>
                <div className="col-12">
                  <Controller
                    name='facilityType'
                    control={control}
                    render={({ onChange, value }) => (
                      <DropdownList
                        value={facilities?.length ? value : []}
                        data={facilities}
                        textField={'name'}
                        valueField={'value'}
                        onChange={(selectedValues) => {
                          onChange(selectedValues);
                        }}
                        placeholder='Select Facility Type'
                      />
                    )}
                  />
                  {errors.facilityType && (
                    <ErrorMessage>{errors.facilityType.message}</ErrorMessage>
                  )}
                </div>
              </div>
            </div>}
          <div className="col-4 mb-4">
            <div className="row">
              <div className="col-12 font-normal font-weight-semibold mb-2">
                Interior Type
              </div>
              <div className="col-12">
                <Controller
                  name='interiorType'
                  control={control}
                  render={({ onChange, value }) => (
                    <Multiselect
                      value={value}
                      data={Object.entries(InteriorType).map(([name, val]) => ({
                        name: addSpacesToEnumValue(name),
                        value: val,
                      }))}
                      textField='name'
                      valueField='value'
                      placeholder='Select'
                      onChange={(val) => {
                        onChange(val?.map((x) => x.value));
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-4 mb-4" style={{paddingBottom: '13px'}}>
            <span className="mr-4">
              <Controller
                control={control}
                name='includeGradeC'
                render={({ onChange, value, name }) => (
                  <Fragment>
                    <input
                      type='checkbox'
                      id={name}
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                    <label htmlFor={name}>Include Grade C</label>
                  </Fragment>
                )}
              />
            </span>
            <span>
              <Controller
                control={control}
                name='includeLeaseExpiry'
                render={({ onChange, value, name }) => (
                  <Fragment>
                    <input
                      type='checkbox'
                      id={name}
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                    <label htmlFor={name}>Include Lease Expiry Options</label>
                  </Fragment>
                )}
              />
            </span>
          </div>

          <div className='col-12 font-normal font-weight-semibold pb-3'>
            {!hideButtons && (
              <>
                <button
                  type='button'
                  onClick={() => {
                    reset();
                    props.onClose?.();
                    setAdvancedSearch(false);
                  }}
                  className='btn btn-outline-secondary font-normal font-weight-bold mr-3 px-4 mt-3'
                >
                  Cancel
                </button>

                <button
                  type='submit'
                  className='btn btn-primary font-normal font-weight-bold px-4 mt-3'
                  disabled={saving}
                >
                  Search
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </form>
  );
};
export default SearchRequirement;
